import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { createEntitiesIndexSelector } from '@iconik-io/shared-lib/src/redux/entities_index/selectors';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { seekableMediaTypes } from '@iconik-io/shared-lib/src/utils/media_type';

import selectMergedPermissions from '../../components/permissions_manager/selectors';
import { SEGMENT_TYPE_COMMENT } from '../../components/segment_form/constants';
import type { AppState } from '../../store/types';
import { selectUserRoles } from '../../utils/get_user_roles';
import { selectAssetProxy } from '../pages/asset/selectors';
import { selectIsSharePage } from '../share/selectors';

const getSegment = (_state: AppState, segment: SegmentEntityWithIdNormalized) =>
  segment;

const getAssetPermissions = (
  state: AppState,
  segment: SegmentEntityWithIdNormalized,
) => selectMergedPermissions(state, OBJECT_TYPE_ASSETS, segment.asset_id);

const getUserData = (state: AppState) => state.authReducer.userData;
const getShare = (state: AppState) => state.share.share;

export const selectCanEditSegment = createCachedSelector(
  selectUserRoles,
  getAssetPermissions,
  getSegment,
  getUserData,
  getShare,
  selectIsSharePage,
  (roles, assetPermissions, segment, userData, share, isSharePage) => {
    if (segment.segment_type === SEGMENT_TYPE_COMMENT) {
      if (!roles.canWriteSegments) {
        return false;
      }

      // the registered user commented on a share link
      if (segment.share_user_email === userData.email) {
        return true;
      }

      if (isSharePage && !share?.allow_comments) {
        return false;
      }

      // owner
      if (userData.id === segment.user_id) {
        return true;
      }

      // the user can edit other users comments. Used in some workflows for grammar corrections.
      if (roles.canEditOtherUsersComments) {
        return true;
      }

      return Boolean(userData.is_admin);
    }

    return Boolean(assetPermissions?.write && roles.canWriteSegments);
  },
)((_state, segment) => segment.id);

export const selectIsTimeRangedAsset = createSelector(
  (state: AppState, assetId: string | undefined) =>
    assetId ? state.entities.assets[assetId] : undefined,
  selectAssetProxy,
  (asset, proxy) =>
    asset?.type === 'PLACEHOLDER' ||
    seekableMediaTypes.has(getProxyMediaType(proxy)),
);

export const selectSegmentsBySegmentType = createEntitiesIndexSelector(
  'segments',
  'segment_type',
);
